<template>
  <div class="accordion-item" :class="{ open: internalOpen }">
    <div class="accordion-top">
      <div class="accordion-header grow" @click="internalOpen = !internalOpen">
        <slot name="title">
          {{ title }}
        </slot>
        <chevron class="icon" />
      </div>
      <TrashIcon v-if="deleteable" class="trash" @click="emit('delete')" />
    </div>

    <collapsable v-model:open="internalOpen">
      <div class="accordion-content">
        <slot>{{ content }}</slot>
      </div>
    </collapsable>
  </div>
</template>

<script lang="ts" setup>
import { watch } from 'vue';
import Chevron from '@images/chevron.svg?component';
import TrashIcon from '@images/trash.svg?component';
import { useVModel } from '@vueuse/core';
import { v4 as uuidv4 } from 'uuid';
import Collapsable from '@utils/Collapsable.vue';
import { useAccordion } from './injects.js';

interface Props {
  id?: string;
  title?: string;
  content?: string;
  open?: boolean;
  deleteable?: boolean;
}

const emit = defineEmits(['update:open', 'delete']);
const props = withDefaults(defineProps<Props>(), { id: () => uuidv4(), open: false, deleteable: false });
const internalOpen = useVModel(props, 'open', emit, { passive: true });

const accordion = useAccordion();

watch(() => accordion?.openIds, (newOpenIds) => {
  if (!newOpenIds) return;
  const entry = Object.entries(newOpenIds).find(([id]) => id === props.id);
  if (!entry || entry[1] === internalOpen.value) return;
  [, internalOpen.value] = entry;
}, { deep: true });

watch(internalOpen, (newOpen) => {
  if (!accordion) return;
  accordion.setOpen(props.id, newOpen);
}, { immediate: true });
</script>

<style lang="scss" scoped>
.accordion-top {
  @apply flex items-center;
  //@apply border-b border-b-element-neutral;
  @apply border border-element-neutral;
  @apply rounded-tr-sm rounded-tl-sm;
  background-color: var(--color-table-head-gray-100);

  .accordion-header {
    @apply flex items-center;
    @apply font-bold;
    @apply py-3 px-2;
    @apply cursor-pointer;
  }

  .trash {
    @apply w-6 h-6;
    @apply mx-2;
    @apply cursor-pointer;

    &:hover {
      :deep(path) {
        stroke: var(--color-red-100);
      }
    }
  }
}

.icon {
  @apply ml-auto mr-2;
  max-width: px(13);
  max-height: px(13);
  transition: transform var(--speed-fast) ease;
  transform: rotate(-90deg);
}

.accordion-item.open .icon {
  transform: rotate(-180deg);
}

.accordion-content {
  @apply py-5 px-4;
  @apply border-b border-b-element-neutral;
  @apply border-x border-x-element-neutral;
  @apply rounded-br-sm rounded-bl-sm;
  background-color: var(--color-background-light-100);

  --input-bg: var(--color-white);
}
</style>
